package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.ui.runtime.animation.AnimationHandle
import top.wcpe.allincore.ui.runtime.animation.AnimationScheduler
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentityMap
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal

/**
 * Slider 与响应式 state 的双向绑定处理器。
 *
 * 抽出自 [top.wcpe.allincore.ui.runtime.ir.IrInterpreter]：原本 90 行 Slider 状态绑定 / 用户输入回写 /
 * transition 接管的逻辑与解释器主流程混在一起，是该类的最大耦合点。本类把它们集中并通过 [ValueMemo]
 * 与上层解耦——IrInterpreter 不再关心 Slider 的内部状态机。
 *
 * 状态机要点（参见 README §动画语义）：
 * - `applyingStateValue`：写状态时设置；防止 onChange 回调反向触发 set，造成无限递归
 * - `localValueWrite`：用户拖动时记录的本地值；后续 state 同步触达时识别为「我自己的」回写并跳过动画
 * - `activeValueTransition`：上一次 state→Slider transition 的句柄，用户再次拖动时取消它
 */
internal class SliderBindingHandler(private val memo: ValueMemo) {

    /** 上层（IrInterpreter）保留的「上一次写入值」回调，用于驱动 transition 的 from / to。 */
    interface ValueMemo {
        fun recall(slider: Slider, key: String): Any?
        fun remember(slider: Slider, key: String, value: Any?)
    }

    private val states = newIdentityMap<Slider, SliderState>()

    /** state → Slider 单向绑定（state 变 → Slider 显示）+ Slider → state 双向回写。 */
    fun bindStateBackedValue(slider: Slider, signal: Signal<Any?>) {
        stateOf(slider).valueBinding = signal
        installChangeHandler(slider)
        val effect = Effect { applyStateBackedValue(slider, signal) }
        slider.lifecycleScope().register(effect)
    }

    /** 注册「Slider 值变 → 触发 action / 动画」的 trigger；不做 state 绑定。 */
    fun bindChangeTrigger(slider: Slider, trigger: UiTask) {
        stateOf(slider).changeTrigger = trigger
        installChangeHandler(slider)
    }

    private fun installChangeHandler(slider: Slider) {
        slider.onChange { ev ->
            val state = stateOf(slider)
            if (state.applyingStateValue) {
                return@onChange
            }
            cancelActiveTransition(state)
            val signal = state.valueBinding
            if (signal != null && signal.get() != ev.newValue) {
                state.localValueWrite = ev.newValue
                signal.set(ev.newValue)
            }
            state.changeTrigger?.run()
        }
    }

    private fun applyStateBackedValue(slider: Slider, signal: Signal<Any?>) {
        val value = coerceValue(signal.get())
        if (consumeLocalWrite(slider, value)) {
            memo.remember(slider, "value", value)
            return
        }
        val previous = memo.recall(slider, "value")
        if (previous != null && previous != value) {
            val transition = slider.animationFor("transition")
            val scheduler = slider.animationScheduler() as AnimationScheduler? // marker 断环，见 AnimationDriverHandle
            if (transition != null && scheduler != null) {
                val handle = scheduler.transition(slider, "value", slider.value(), value, transition)
                if (handle != null) {
                    stateOf(slider).activeValueTransition = handle
                    memo.remember(slider, "value", value)
                    return
                }
            }
        }
        val state = stateOf(slider)
        state.applyingStateValue = true
        try {
            slider.value(value)
        } finally {
            state.applyingStateValue = false
        }
        memo.remember(slider, "value", value)
    }

    private fun consumeLocalWrite(slider: Slider, value: Double): Boolean {
        val state = stateOf(slider)
        val local = state.localValueWrite
        if (local is Number && local.toDouble().compareTo(value) == 0) {
            state.localValueWrite = null
            return true
        }
        return false
    }

    private fun cancelActiveTransition(state: SliderState) {
        val handle = state.activeValueTransition
        if (handle != null && !handle.isCancelled() && !handle.isFinished()) {
            handle.cancel()
        }
        state.activeValueTransition = null
    }

    private fun stateOf(slider: Slider): SliderState = states.getOrPut(slider) { SliderState() }

    private fun coerceValue(value: Any?): Double {
        if (value is Number) {
            return value.toDouble()
        }
        throw IllegalStateException("Slider 绑定的 value state 必须是数字: $value")
    }

    private class SliderState {
        var valueBinding: Signal<Any?>? = null
        var changeTrigger: UiTask? = null
        var localValueWrite: Any? = null
        var activeValueTransition: AnimationHandle? = null
        var applyingStateValue: Boolean = false
    }
}
